import { useEffect, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Star, Check, Broom, Receipt, WifiHigh, CheckCircle } from "@phosphor-icons/react";
import { PORTRAIT, drawPortrait } from "@/lib/portrait";
import { TAG, drawTag } from "@/pages/host/editor/PrintKit";

const SAMPLE_URL = `${typeof window !== "undefined" ? window.location.origin : ""}/sample`;

export function CardMock({ className = "" }) {
  const c = useRef();
  const q = useRef();
  useEffect(() => { document.fonts.ready.then(() => c.current && drawPortrait(c.current, { name: "Kololo Garden Flat", hostName: "Amina", qr: q.current?.querySelector("canvas") })); }, []);
  return (
    <div className={className}>
      <div className="hidden" ref={q}><QRCodeCanvas value={SAMPLE_URL} size={400} marginSize={1} /></div>
      <canvas ref={c} width={PORTRAIT[0]} height={PORTRAIT[1]} className="h-auto w-full rounded-[18px] shadow-[0_40px_80px_-30px_rgba(0,0,0,.8)] ring-2 ring-[#8FA3C7]/40" data-testid="hero-card-mock" />
    </div>
  );
}

export function TagMock({ className = "" }) {
  const c = useRef();
  const q = useRef();
  useEffect(() => { document.fonts.ready.then(() => c.current && drawTag(c.current, "Front door", q.current?.querySelector("canvas"))); }, []);
  return (
    <div className={className}>
      <div className="hidden" ref={q}><QRCodeCanvas value={SAMPLE_URL} size={300} marginSize={1} /></div>
      <canvas ref={c} width={TAG[0]} height={TAG[1]} className="h-auto w-full drop-shadow-[0_30px_40px_rgba(0,0,0,.45)]" data-testid="hero-tag-mock" />
    </div>
  );
}

export function PhoneMock({ className = "" }) {
  return (
    <div className={`rounded-[2.4rem] bg-[#05080F] p-2.5 text-[#0E1729] shadow-[0_50px_90px_-30px_rgba(0,0,0,.8)] ring-1 ring-white/10 ${className}`} data-testid="hero-phone-mock">
      <div className="relative overflow-hidden rounded-[2rem] bg-[#F4F6F8]">
        <div className="absolute left-1/2 top-2 z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-[#05080F]" />
        <div className="relative h-36">
          <img src="https://images.unsplash.com/photo-1658218635253-64728f6234be?w=600&q=70" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <p className="absolute bottom-3 left-4 font-display text-lg text-white">Kololo Garden Flat</p>
        </div>
        <div className="space-y-2.5 p-3">
          <div className="flex items-center gap-2 rounded-2xl bg-[#E3EEE2] p-2.5 text-xs text-[#2F5A2D]"><CheckCircle size={18} weight="fill" /> You're checked in · HK-4F92A1</div>
          <div className="rounded-2xl bg-white p-3">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-[#0E1729]"><WifiHigh size={14} weight="bold" /> Wi-Fi</p>
            <div className="mt-2 flex items-center justify-between rounded-xl bg-[#F4F6F8] px-3 py-2 text-xs"><span className="font-mono">karibu2026</span><span className="font-semibold text-[#0E1729]">Copy</span></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[["Check-in", "14:00"], ["Check-out", "11:00"]].map(([a, b]) => <div key={a} className="rounded-2xl bg-white p-2.5"><p className="text-[10px] text-slate-500">{a}</p><p className="font-display text-lg text-[#0E1729]">{b}</p></div>)}
          </div>
          <div className="rounded-full bg-[#0E1729] py-2.5 text-center text-xs font-semibold text-white">Open in Maps</div>
        </div>
      </div>
    </div>
  );
}

export function InvoiceMock() {
  return (
    <div className="rounded-xl bg-white p-4 text-[11px] text-[#0E1729] shadow-xl" data-testid="mock-invoice">
      <div className="flex items-start justify-between"><span className="font-bold">INVOICE</span><span className="rounded border border-[#3E6B3C] px-1.5 font-bold text-[#3E6B3C]">PAID</span></div>
      <p className="mt-1 text-slate-500">INV-0012 · Daniel Kato</p>
      {[["Accommodation × 3", "540,000"], ["Airport pickup", "60,000"]].map(([a, b]) => <div key={a} className="mt-2 flex justify-between border-b border-slate-100 pb-1"><span>{a}</span><span>{b}</span></div>)}
      <div className="mt-2 flex justify-between font-bold"><span>Total</span><span>UGX 600,000</span></div>
    </div>
  );
}

export function ChecklistMock() {
  return (
    <div className="space-y-1.5 rounded-xl bg-white p-4 text-[11px] text-[#1E3D2D] shadow-xl" data-testid="mock-checklist">
      <p className="flex items-center gap-1 font-bold text-[#2F6B4F]"><Broom /> Housekeeping · 3/4</p>
      {["Change bed sheets", "Fresh towels", "Empty all bins", "Restock soap"].map((t, i) => (
        <div key={t} className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${i < 3 ? "border-[#2F6B4F] bg-[#E9F2EC]" : "border-slate-200"}`}>
          <span className={`grid h-3.5 w-3.5 place-items-center rounded ${i < 3 ? "bg-[#2F6B4F] text-white" : "border border-slate-300"}`}>{i < 3 && <Check size={9} weight="bold" />}</span>{t}
        </div>
      ))}
    </div>
  );
}

export function FeedbackMock() {
  return (
    <div className="rounded-xl bg-white p-4 text-[#0E1729] shadow-xl" data-testid="mock-feedback">
      <div className="flex items-end justify-between"><span className="font-display text-3xl">4.8</span><span className="flex text-[#E4A853]">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} weight="fill" />)}</span></div>
      {[[5, 80], [4, 15], [3, 5]].map(([n, w]) => <div key={n} className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-500"><span>{n}</span><div className="h-1.5 flex-1 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0E1729]" style={{ width: `${w}%` }} /></div></div>)}
      <p className="mt-2 text-[11px] italic text-slate-600">“Spotless flat, the Wi-Fi card saved us!”</p>
    </div>
  );
}

export const ReceiptIcon = Receipt;
